// frontend/src/components/auth/AuthProvider.js
import React, { useState, useEffect, createContext, useContext } from 'react';
import toast from 'react-hot-toast';
import { authAPI, apiUtils } from '../../services/api';
import { clearSession as clearCorvinSession } from '../corvin/session';

// Auth Context
const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Auth Provider Component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isNewRegistration, setIsNewRegistration] = useState(false);
  const [mfaRequired, setMfaRequired] = useState(false);

  useEffect(() => {
    initializeAuth();
  }, []);

  const initializeAuth = async () => {
    try {
      const userData = apiUtils.getCurrentUser();
      const showSubscription = localStorage.getItem('show_subscription_selection');
      
      // Check if this is a new registration that needs subscription
      if (showSubscription === 'true') {
        setIsNewRegistration(true);
      }
      
      // With httpOnly cookies, we try to fetch profile to check auth status
      // If cookie is valid, profile will succeed; if not, it will fail
      if (userData) {
        try {
          console.log('🔍 Validating session via profile...');
          const profile = await authAPI.getProfile();
          console.log('📋 Profile data received:', profile);
          setUser(profile);
          setIsAuthenticated(true);
          
          // Update localStorage with fresh data
          apiUtils.setAuthData(profile);
          
          // If user is authenticated but has no subscription flag, clear any stale flags
          if (!showSubscription) {
            localStorage.removeItem('show_subscription_selection');
            localStorage.removeItem('user_type_selected');
            setIsNewRegistration(false);
          }
        } catch (error) {
          // Cookie invalid or expired, clear auth data
          console.log('Session expired or invalid');
          apiUtils.clearAuthData();
          setUser(null);
          setIsAuthenticated(false);
          setIsNewRegistration(false);
          localStorage.removeItem('show_subscription_selection');
          localStorage.removeItem('user_type_selected');
        }
      } else {
        // No user data, clear any stale flags
        localStorage.removeItem('show_subscription_selection');
        localStorage.removeItem('user_type_selected');
        setIsNewRegistration(false);
      }
    } catch (error) {
      console.error('Auth initialization error:', error);
    } finally {
      setLoading(false);
    }
  };

  const login = async (credentials) => {
    try {
      const response = await authAPI.login(credentials);
      
      // Check if MFA is required
      if (response.mfa_required) {
        setMfaRequired(true);
        return { mfa_required: true };
      }
      
      // Login successful - user data is in response, cookies set by server
      const { user: userData, csrf_token, access_token, token, refresh_token } = response;
      
      if (userData) {
        apiUtils.setAuthData(userData, csrf_token, access_token || token, refresh_token);
        setUser(userData);
        setIsAuthenticated(true);
        setIsNewRegistration(false);
        setMfaRequired(false);
        
        // Clear any subscription selection flags for existing users
        localStorage.removeItem('show_subscription_selection');
        localStorage.removeItem('user_type_selected');
        
        toast.success('Welcome back!');
      }
      
      return userData;
    } catch (error) {
      const errorMessage = apiUtils.formatErrorMessage(error);
      toast.error(errorMessage);
      throw new Error(errorMessage);
    }
  };

  const verifyMfa = async (code, useBackup = false) => {
    try {
      const response = await authAPI.verifyMfa(code, useBackup);
      const { user: userData, csrf_token, access_token, token, refresh_token } = response;
      
      if (userData) {
        apiUtils.setAuthData(userData, csrf_token, access_token || token, refresh_token);
        setUser(userData);
        setIsAuthenticated(true);
        setMfaRequired(false);
        
        toast.success('Welcome back!');
      }
      
      return userData;
    } catch (error) {
      const errorMessage = apiUtils.formatErrorMessage(error);
      toast.error(errorMessage);
      throw new Error(errorMessage);
    }
  };

  const register = async (userData) => {
    try {
      const response = await authAPI.register(userData);
      const { user: newUser, csrf_token, access_token, token, refresh_token } = response;
      
      // Store user data (tokens in httpOnly cookies)
      apiUtils.setAuthData(newUser, csrf_token, access_token || token, refresh_token);
      setUser(newUser);
      setIsAuthenticated(true);
      
      // Mark this as a new registration
      setIsNewRegistration(true);
      localStorage.setItem('show_subscription_selection', 'true');
      localStorage.setItem('user_type_selected', userData.user_type);
      
      toast.success('Account created! Please select a subscription plan.');
      
      return {
        ...newUser,
        isNewRegistration: true
      };
    } catch (error) {
      const errorMessage = apiUtils.formatErrorMessage(error);
      toast.error(errorMessage);
      throw new Error(errorMessage);
    }
  };

  const logout = async () => {
    try {
      // Call logout endpoint - server will clear cookies
      await authAPI.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      apiUtils.clearAuthData();
      setUser(null);
      setIsAuthenticated(false);
      setIsNewRegistration(false);
      setMfaRequired(false);
      localStorage.removeItem('show_subscription_selection');
      localStorage.removeItem('user_type_selected');
      // Corvin caches this user's tip for the browser session.
      clearCorvinSession();
      toast.success('Logged out successfully');
    }
  };

  const updateUser = (userData) => {
    setUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));
  };

  const refreshUser = async () => {
    try {
      const profile = await authAPI.getProfile();
      setUser(profile);
      localStorage.setItem('user', JSON.stringify(profile));
      
      // Note: Subscription cache will be refreshed on next component mount
      
      return profile;
    } catch (error) {
      console.error('Failed to refresh user:', error);
      throw error;
    }
  };

  const clearNewRegistrationFlag = () => {
    setIsNewRegistration(false);
    localStorage.removeItem('show_subscription_selection');
    localStorage.removeItem('user_type_selected');
  };

  const value = {
    user,
    loading,
    isAuthenticated,
    isNewRegistration,
    mfaRequired,
    login,
    verifyMfa,
    register,
    logout,
    updateUser,
    refreshUser,
    clearNewRegistrationFlag
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};